import Cookies from "js-cookie";

export const API_ENDPOINT: string = process.env.NODE_ENV === "development" ? "http://" + window.location.hostname + ":8000" : "";
export const UPLOAD_ENDPOINT: string = API_ENDPOINT + "/upload/upload/";

export enum ErrorType {
    GENERIC_ERROR,
    NETWORK_ERROR,
    VALIDATION_ERROR,
    SESSION_EXPIRED,
    FORBIDDEN,
}

export interface IRequestError {
    error_type: ErrorType,
    message: string;
    params?: any;
}

async function errorFromRequest(resp: Response): Promise<IRequestError> {
    let error_type = ErrorType.GENERIC_ERROR;
    let message: undefined | string = undefined;
    let params: any = { non_field_errors: undefined, detail: undefined };
    let text = await resp.text();
    try {
        params = JSON.parse(text);
    } catch (err) {
        params.detail = text;
    }
    switch (resp.status) {
        case 400:
            error_type = ErrorType.VALIDATION_ERROR;
            message =
                params?.non_field_errors
                || params?.detail
                || params?.error
                || "Validation error";
            break;
        case 401:
            error_type = ErrorType.SESSION_EXPIRED;
            message = params?.detail;
            break;
        case 403:
            error_type = ErrorType.FORBIDDEN;
            message = params?.detail;
            break;
        case 500:
            error_type = ErrorType.GENERIC_ERROR;
            message = params?.detail;
            break;
    }
    return {
        error_type,
        message: message ? message : resp.statusText,
        params
    }
}

export async function api<T>(url: string, method?: string, data?: any): Promise<T> {

    try {
        const request_method = method ? method : "GET";

        let request_url = API_ENDPOINT + url;

        let headers: HeadersInit = {
            'Content-Type': 'application/json',
            'X-CSRFToken': Cookies.get('csrftoken') || window.csrftoken,
        };

        let request: RequestInit = {
            headers: headers,
            method: request_method,
            referrerPolicy: 'origin',
        };

        switch (method) {
            case "POST":
            case "PUT":
                request.body = JSON.stringify(data);
                break;

            default:
                let requestData: any = {}
                for (let key in data) {
                    let value = data[key];
                    if (value) {
                        if (value instanceof Object) value = JSON.stringify(value);
                        requestData[key] = value;
                    }
                };
                request_url = request_url + "?" + new URLSearchParams(requestData).toString();
                break;
        }

        let response = await fetch(request_url, request);

        if (!response.ok) {
            const error = await errorFromRequest(response);
            throw error;
        }

        const empty_response_statuses = [204, 205];
        if (empty_response_statuses.indexOf(response.status) > -1) {
            return {} as Promise<T>;
        }

        return response.json() as Promise<T>;
    }
    catch (err) {
        let error: IRequestError = err as IRequestError;
        if (!error) error = {
            error_type: ErrorType.NETWORK_ERROR,
            message: (err as Error).message,
        };
        throw error;
    };
}

export async function apiGet<T>(url: string, data?: any): Promise<T> {
    return api<T>(url, "GET", data);
}

export async function apiPost<T>(url: string, data?: any): Promise<T> {
    return api<T>(url, "POST", data);
}

export async function apiPut<T>(url: string, data?: any): Promise<T> {
    return api<T>(url, "PUT", data);
}

export async function apiDelete<T>(url: string): Promise<T> {
    return api<T>(url, "DELETE");
}


export async function apiGetChunk(url: string, opts: any) {
    let onProgress = opts ? opts.onProgress : undefined;
    let urlWithParams = API_ENDPOINT + url;
    if (opts.params) {
        urlWithParams = urlWithParams + "?" + new URLSearchParams(opts.params).toString()
    }
    const response = await fetch(urlWithParams, opts);
    const reader = response.body!.getReader();
    let result = await reader.read();

    while (!result.done) {
        const value = result.value;
        if (typeof onProgress === "function") {
            onProgress(value)
        }
        result = await reader.read();
    }

    return response;
}
